import React from 'react';
import { Button } from './Button';
import { X, AlertTriangle, CheckCircle, Info, HelpCircle } from 'lucide-react';

export type DialogType = 'success' | 'error' | 'confirm' | 'info';

export interface DialogProps {
  isOpen: boolean;
  type: DialogType;
  title: string;
  message: string;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
}

export const Dialog: React.FC<DialogProps> = ({ 
  isOpen, type, title, message, onClose, onConfirm, 
  confirmText = "Đồng ý", cancelText = "Hủy bỏ" 
}) => {
  if (!isOpen) return null;

  const config = {
    success: { icon: CheckCircle, color: 'text-green-600', bgIcon: 'bg-green-100' },
    error: { icon: AlertTriangle, color: 'text-red-600', bgIcon: 'bg-red-100' },
    confirm: { icon: HelpCircle, color: 'text-blue-600', bgIcon: 'bg-blue-100' },
    info: { icon: Info, color: 'text-slate-600', bgIcon: 'bg-slate-100' }
  };

  const { icon: Icon, color, bgIcon } = config[type];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Dialog Panel */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all animate-in zoom-in-95 fade-in duration-200 border border-slate-100">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-full hover:bg-slate-100"
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className={`w-12 h-12 rounded-full ${bgIcon} flex items-center justify-center mb-4 ring-4 ring-white shadow-sm`}>
              <Icon className={`w-6 h-6 ${color}`} />
            </div>
            
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {title}
            </h3>
            
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              {message}
            </p>

            <div className="flex gap-3 w-full justify-center">
              {type === 'confirm' ? (
                <>
                  <Button variant="ghost" onClick={onClose} className="flex-1">
                    {cancelText}
                  </Button>
                  <Button variant="primary" onClick={() => { onConfirm?.(); onClose(); }} className="flex-1 shadow-md shadow-blue-200">
                    {confirmText}
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={onClose} className="min-w-[120px] shadow-sm">
                  Đóng
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};